import { action } from '@ember/object';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import { tracked } from '@glimmer/tracking';
import { addDays, endOfDay, format, isToday, isYesterday, startOfDay, subDays, subMinutes } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import type AbilitiesService from 'ember-can/services/abilities';
import { dropTask, task } from 'ember-concurrency';
import type { ColumnHeader } from 'garaje/components/ui/table';
import type EmployeeModel from 'garaje/models/employee';
import type FloorModel from 'garaje/models/floor';
import type GenericResourceModel from 'garaje/models/generic-resource';
import DeskReservationsController from 'garaje/pods/desks/reservations/controller';
import type AjaxService from 'garaje/services/ajax';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type MetricsService from 'garaje/services/metrics';
import type StateService from 'garaje/services/state';
import type { ResourcesQueryFilter } from 'garaje/types/generic-resources/QueryFilter';
import { modifyDateInTimeZone } from 'garaje/utils/date-fns-tz-utilities';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import urlBuilder from 'garaje/utils/url-builder';
import { defer, resolve } from 'rsvp';

import type { DesksReservationsRouteModel } from './route';

interface ProfileData {
  id: number;
  fullName: string;
  email: string;
  profilePhotoUrl: string;
}

interface ResourceReservationData {
  reservationId: number;
  resourceId: number;
  resourceTypeId: number;
  relationId: string;
  startTime: string;
  endTime: string;
  reservedFor: string;
  reservedForProfileData: ProfileData | null;
  reservedBy: string;
  cancelledAt: string;
  cancelReason: string;
  bookingSource: string;
  reservationCreatedAt: string;
  reservationUpdatedAt: string;
  resourceName: string;
  floorId: number;
}

interface ResourceReservationResponse {
  data: ResourceReservationData[];
}

enum LOG_SORT_KEYS {
  'START_TIME_ASC' = 'START_TIME_ASC',
  'START_TIME_DESC' = 'START_TIME_DESC',
  'END_TIME_ASC' = 'END_TIME_ASC',
  'END_TIME_DESC' = 'END_TIME_DESC',
  'CREATED_AT_ASC' = 'CREATED_AT_ASC',
  'CREATED_AT_DESC' = 'CREATED_AT_DESC',
  'RESOURCE_NAME_ASC' = 'RESOURCE_NAME_ASC',
  'RESOURCE_NAME_DESC' = 'RESOURCE_NAME_DESC',
}

export interface ReservationLogQueryFilter {
  'start-time'?: string;
  'end-time'?: string;
  'resource-type-id'?: number;
  'employee-id'?: string;
  'floor-id'?: string;
  'resource-id'?: string;
  limit?: number; // default is 25 on the backend
  offset?: number; // default is 0 on the backend
  'sort-by'?: LOG_SORT_KEYS; // default is START_TIME_ASC on the backend
}

interface ReservationLogRecord {
  id: number;
  fullName: string;
  email: string;
  profilePhotoUrl: string;
  resourceType: string;
  resourceName: string;
  date: string;
  time: string;
  cancelledAt: string | null;
  resourceId: number;
}

const DEFAULT_TIMEZONE = Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone || 'America/Los_Angeles';
export default class ReservationLogController extends DeskReservationsController {
  @service declare ajax: AjaxService;
  @service declare state: StateService;
  @service declare store: Store;
  @service declare flashMessages: FlashMessagesService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare abilities: AbilitiesService;
  @service declare metrics: MetricsService;

  @tracked activePage: 'desks' | 'parking' = 'desks';
  @tracked reservationRecords: ReservationLogRecord[] = [];
  @tracked resourceReservations: Array<ResourceReservationData> = [];
  @tracked latestResourceReservationsData: Array<ResourceReservationData> = [];
  @tracked resourceReservationsPage: number = 0;
  @tracked resourceReservationsLimit: number = 100;
  @tracked selectedReservationId: number | null = null; // updated when a row is selected through the action menu

  // although we're initializing the start ane end date here,
  // these values are updated in resourceSetStartAndEndTime()
  // which is called in the setupController hook inside the route file
  @tracked resourceStartDate: Date = new Date();
  @tracked resourceEndDate: Date = new Date();
  @tracked resourceSelectedDateRange = 'Today';
  @tracked resourceShowCalendar = false;

  // filters
  @tracked resourceSelectedEmployees: EmployeeModel[] = [];
  @tracked resourceSelectedEmployeeIds: number[] = [];
  @tracked resourceSelectedFloors: FloorModel[] = [];
  @tracked resourceSelectedFloorIds: number[] = [];

  // the latest resource reservations data that will be used
  // to calculate pagination and load more data
  @tracked resourcesResponse: Array<GenericResourceModel> = [];
  @tracked resources: Array<GenericResourceModel | string> = [];
  @tracked resourcesCount: number = 0;
  @tracked selectedResources: GenericResourceModel[] = [];
  @tracked selectedResourceIds: number[] = [];
  @tracked resourcesPage: number = 0;
  @tracked resourcesLimit: number = 100;
  @tracked resourceReservationToEdit: ResourceReservationData | undefined = undefined;

  declare model: DesksReservationsRouteModel;

  queryParams = [
    'startDate',
    'endDate',
    'selectedDateRange',
    'selectedEmployeesIds',
    'selectedFloorsIds',
    'selectedDesksIds',
    'sort',
    'activePage',
  ];

  resetResourceReservations(): void {
    this.resourceReservations = [];
    this.latestResourceReservationsData = [];
    this.resourceReservationsPage = 0;
  }

  getResourceTypeLabel(resourceTypeId: number): string {
    switch (resourceTypeId) {
      case 1:
        return 'Desk';
      case 2:
        return 'Parking';
      default:
        return 'Resource';
    }
  }

  get hasMoreResourcesPages(): boolean {
    return this.resourcesResponse.length === this.resourcesLimit;
  }

  get hasMoreResourceReservationsPage(): boolean {
    return this.latestResourceReservationsData.length === this.resourceReservationsLimit;
  }

  get resourceReservationActions(): {
    label: string;
    className?: string;
    onAction: (record: ReservationLogRecord) => void;
  }[] {
    return this.featureFlags.isEnabled('delegated-resource-booking')
      ? [
          {
            label: 'Edit reservation',
            className: 'edit-reservation',
            onAction: (record: ReservationLogRecord) => {
              this.editReservationModalTask.perform(record);
            },
          },
          {
            label: 'Delete reservation',
            className: 'delete-reservation text-red-50',
            onAction: (record: ReservationLogRecord) => this.showDeleteConfirmationTask.perform(record),
          },
        ]
      : [];
  }

  isReservationCancelled(record: ReservationLogRecord): boolean {
    return record.cancelledAt !== null;
  }

  @dropTask
  delegatedModalTask: {
    perform(record: ReservationLogRecord): Generator<Promise<unknown>, unknown, unknown>;
  } = {
    *perform(
      this: {
        context: {
          resourceReservationToEdit: ResourceReservationData | undefined;
          resourceReservations: ResourceReservationData[];
          resetResourceReservations: () => void;
          reloadResourceReservations: (resource: string) => void;
          loadReservationsRestartableTask: {
            perform: () => Promise<void>;
          };
        };
        abort: () => void;
        continue: (resource: string) => void;
      },
      record: ReservationLogRecord,
    ): Generator<Promise<unknown>, unknown, unknown> {
      this.context.resourceReservationToEdit = this.context.resourceReservations.find(
        (res) => res.reservationId == record.id,
      );
      const deferred = defer();
      this.abort = () => {
        this.context.resetResourceReservations();
        void this.context.loadReservationsRestartableTask.perform();
        deferred.resolve(false);
      };
      this.continue = (resource: string) => {
        this.context.reloadResourceReservations(resource);
        void this.context.loadReservationsRestartableTask.perform();
        deferred.resolve(true);
      };
      return yield deferred.promise;
    },
  };

  @dropTask
  editReservationModalTask: {
    perform(record: ReservationLogRecord): Generator<Promise<unknown>, unknown, unknown>;
  } = {
    *perform(
      this: {
        context: ReservationLogController;
        abort: () => void;
        continue: (resource: string) => void;
      },
      record: ReservationLogRecord,
    ): Generator<Promise<unknown>, unknown, unknown> {
      this.context.resourceReservationToEdit = this.context.resourceReservations.find(
        (res) => res.reservationId == record.id,
      );
      const deferred = defer();
      this.abort = () => {
        this.context.resetResourceReservations();
        this.context.resourceReservationToEdit = undefined;
        deferred.resolve(false);
      };
      this.continue = (resource: string) => {
        this.context.resourceReservationToEdit = undefined;
        this.context.reloadResourceReservations(resource);
        deferred.resolve(true);
      };
      return yield deferred.promise;
    },
  };

  loadResourcesTask = task({ drop: true }, async (): Promise<void> => {
    try {
      const queryFilters = {
        'location-id': this.model?.currentLocation?.id,
        limit: this.resourcesLimit,
        offset: this.resourcesPage * this.resourcesLimit,
      } as ResourcesQueryFilter;

      if (this.resourceSelectedFloorIds.length > 0) {
        queryFilters['floor'] = this.resourceSelectedFloorIds.join(',');
      }

      const response = await this.store.query('generic-resource', queryFilters);
      this.resourcesResponse = response.slice();

      if (this.resources.length) {
        // remove the last element which is just a 'loadMore' placeholder
        this.resources.pop();
      }

      const resourcePromises = this.selectedResourceIds.map((resourceId) => {
        return this.store.findRecord('generic-resource', resourceId);
      });

      const selectedResources = await Promise.all(resourcePromises);
      const combinedResources = [
        ...this.resources,
        ...this.resourcesResponse,
        ...selectedResources,
      ] as Array<GenericResourceModel>;

      const resourcesMap = new Map();
      combinedResources.forEach((resource) => {
        // eslint-disable-next-line ember/use-ember-get-and-set
        resourcesMap.set(resource.id, resource);
      });

      const uniqueResources = Array.from(resourcesMap.values()) as Array<GenericResourceModel | string>;

      this.resourcesPage++;
      if (this.hasMoreResourcesPages) {
        uniqueResources.push('loadMore');
      }
      this.resources = uniqueResources;
    } catch (error) {
      return Promise.reject(error as Error);
    }
  });

  loadMoreResourcesTask = task({ drop: true }, async (): Promise<void> => {
    if (this.hasMoreResourcesPages) {
      try {
        await this.loadResourcesTask.perform();
      } catch (_) {
        this.resourcesPage--;
      }
    }
  });

  loadResourceReservationsTask = task({ restartable: true }, async (): Promise<void> => {
    try {
      const queryFilters: ReservationLogQueryFilter = {
        'start-time': this.resourceStartDate.toISOString(),
        'end-time': this.resourceEndDate.toISOString(),
        'employee-id': this.resourceSelectedEmployeeIds.join(','),
        'floor-id': this.resourceSelectedFloorIds.join(','),
        'resource-id': this.selectedResourceIds.join(','),
        // We're only loading parking reservations right now in this view. When we start loading other resources, we'll need to get the resource type id and pass that in.
        'resource-type-id': 2,
        limit: this.resourceReservationsLimit,
        offset: this.resourceReservationsPage * this.resourceReservationsLimit,
        'sort-by': LOG_SORT_KEYS.START_TIME_ASC,
      };

      const reservationLogEndpoint = urlBuilder.rms.reservationLog(this.model?.currentLocation?.id, queryFilters);
      const response = await this.ajax.request(reservationLogEndpoint, {
        type: 'GET',
        headers: { Accept: 'application/vnd.api+json' },
      });

      const { data } = response as ResourceReservationResponse;
      this.latestResourceReservationsData = data;
      this.resourceReservations = [...this.resourceReservations, ...data];
      this.resourceReservationsPage++;

      this.createTableRecords(this.resourceReservations);
    } catch (error) {
      return Promise.reject(error as Error);
    }
  });

  @action
  reloadResourceReservations(resource?: string): void {
    this.resetResourceReservations();
    void this.loadResourceReservationsTask.perform();
    if (resource) {
      const page = resource === 'desk' ? 'desks' : 'parking';
      this.setActivePage(page);
    }
  }

  @dropTask
  showDeleteConfirmationTask: {
    perform(record: ReservationLogRecord): Generator<Promise<unknown>, unknown, unknown>;
  } = {
    *perform(
      this: { context: ReservationLogController; abort?: () => void; continue?: () => Promise<void> },
      record: ReservationLogRecord,
    ): Generator<Promise<unknown>, unknown, unknown> {
      this.context.selectedReservationId = record.id;
      const deferred = defer();
      this.abort = () => {
        this.context.clearSelectedReservation();
        deferred.resolve(false);
      };
      this.continue = async () => {
        try {
          const url = urlBuilder.rms.deleteResourceReservation(record.id);
          await this.context.ajax.request(url, {
            type: 'DELETE',
            contentType: 'application/vnd.api+json',
          });
          this.context.metrics.trackEvent('Resource reservation deleted in log', {
            resourceType: 'parking',
            reservationId: record.id,
            isDbeam: false,
          });
          this.context.flashMessages.showAndHideFlash('success', 'Reservation deleted');
          this.context.reloadResourceReservations();
        } catch (error) {
          const errorText = parseErrorForDisplay(error);
          this.context.flashMessages.showFlash('error', errorText);
        }
        deferred.resolve(true);
      };
      return yield deferred.promise;
    },
  };

  createTableRecords(resourceReservations: ResourceReservationData[]): void {
    this.reservationRecords = resourceReservations.map((reservation) => {
      const { startTime: startTimeString, endTime: endTimeString } = reservation;

      const startTime = new Date(startTimeString);
      const endTime = new Date(endTimeString);

      const locationStartDate = subMinutes(startTime, this.state.minutesBetweenTimezones(startTime));
      const formattedStartDate = format(locationStartDate, 'M/dd/yyyy');

      const locationEndDate = subMinutes(endTime, this.state.minutesBetweenTimezones(endTime));

      const formattedStartTime = format(locationStartDate, '	p');
      const formattedEndTime = format(locationEndDate, 'p');

      return {
        id: reservation.reservationId,
        fullName: reservation.reservedForProfileData?.fullName,
        email: reservation.reservedForProfileData?.email,
        profilePhotoUrl: reservation.reservedForProfileData?.profilePhotoUrl,
        resourceType: this.getResourceTypeLabel(reservation.resourceTypeId),
        resourceName: reservation.resourceName,
        date: isToday(locationStartDate) ? 'Today' : isYesterday(locationStartDate) ? 'Yesterday' : formattedStartDate,
        time: `${formattedStartTime} - ${formattedEndTime}`,
        cancelledAt: reservation.cancelledAt || null,
        resourceId: reservation.resourceId,
      } as ReservationLogRecord;
    });
  }

  get resourceTabs(): Record<string, unknown>[] {
    const desksTab = {
      id: 'desks',
      label: 'Desks',
    };

    if (this.abilities.can('use parking resource')) {
      return [
        desksTab,
        {
          id: 'parking',
          label: 'Parking',
        },
      ];
    }

    return [desksTab];
  }

  get resourceReservationTableColumns(): ColumnHeader[] {
    return [
      {
        key: 'employee',
        label: 'Reservation for',
        sortable: false,
        componentOptions: {
          template: 'ui/table/table-cell-user-profile',
        },
      },
      {
        key: 'resourceType',
        label: 'Category',
        sortable: true,
      },
      {
        key: 'resourceName',
        label: 'Resource name',
        sortable: true,
      },
      {
        key: 'date',
        label: 'Date',
        sortable: false,
      },
      {
        key: 'time',
        label: 'Time',
        sortable: false,
      },
    ];
  }

  /* Date Time functions */
  get timezone(): string {
    return this.state.currentLocation.timezone || DEFAULT_TIMEZONE;
  }

  get resourceStartDateFormat(): string {
    return formatInTimeZone(new Date(this.resourceStartDate), this.timezone, 'MM/dd/yyyy');
  }

  get resourceEndDateFormat(): string {
    return formatInTimeZone(new Date(this.resourceEndDate), this.timezone, 'MM/dd/yyyy');
  }

  @action
  loadMoreResourceReservationsTask(): Promise<void> {
    if (this.hasMoreResourceReservationsPage) {
      return this.loadResourceReservationsTask.perform();
    } else {
      return resolve();
    }
  }

  @action
  resourceOnDateRangeSelect(option: string): void {
    this.resourceSelectedDateRange = option;

    let start = new Date();
    let end = new Date();

    switch (option) {
      case 'Yesterday':
        start = subDays(start, 1);
        end = subDays(end, 1);
        break;
      case 'Tomorrow':
        start = addDays(start, 1);
        end = addDays(end, 1);
        break;
      case 'Past 7 days':
        start = subDays(start, 7);
        break;
      case 'Past 14 days':
        start = subDays(start, 14);
        break;
      case 'Past 30 days':
        start = subDays(start, 30);
        break;
      case 'Next 7 days':
        end = addDays(end, 7);
        break;
      case 'Next 14 days':
        end = addDays(end, 14);
        break;
      case 'Next 30 days':
        end = addDays(end, 30);
        break;
      case 'Custom range':
        return;
    }

    this.resourceStartDate = modifyDateInTimeZone(start, this.timezone, startOfDay);
    this.resourceEndDate = modifyDateInTimeZone(end, this.timezone, endOfDay);

    this.resetResourceReservations();
    void this.loadResourceReservationsTask.perform();
  }

  @action
  resourceDidSelectDateRange(startDate: Date, endDate: Date): void {
    this.resourceStartDate = startDate;
    this.resourceEndDate = endDate;
    this.selectedDateRange = 'Custom range';

    this.resetResourceReservations();
    void this.loadResourceReservationsTask.perform();
  }

  // this gets called in the setupController hook inside the route file
  @action
  resourceSetStartAndEndTime(): void {
    this.resourceStartDate = modifyDateInTimeZone(new Date(), this.timezone, startOfDay);
    this.resourceEndDate = modifyDateInTimeZone(new Date(), this.timezone, endOfDay);
  }

  @action
  resourceSetStartDate(date: Date): void {
    this.resourceStartDate = date;
  }

  @action
  resourceSetEndDate(date: Date): void {
    this.resourceEndDate = date;
  }

  @action
  resourceSetSelectedEmployees(selectedEmployees: EmployeeModel[]): void {
    this.resourceSelectedEmployees = selectedEmployees;
    this.resourceSelectedEmployeeIds = this.getResourceIds(selectedEmployees) as number[];

    this.resetResourceReservations();
    void this.loadResourceReservationsTask.perform();
  }

  @action
  resourceSetSelectedFloors(selectedFloors: FloorModel[]): void {
    this.resourceSelectedFloors = selectedFloors;
    this.resourceSelectedFloorIds = this.getResourceIds(selectedFloors) as number[];

    this.resetResourceReservations();
    void this.loadResourceReservationsTask.perform();

    // need to also clear the resources state since we need to
    // reload the resources based on the selected floors
    this.resources = [];
    this.resourcesPage = 0;
    void this.loadResourcesTask.perform();
  }

  @action
  setSelectedResources(selectedResources: GenericResourceModel[]): void {
    this.selectedResources = selectedResources;
    this.selectedResourceIds = this.getResourceIds(selectedResources) as number[];

    this.resetResourceReservations();
    void this.loadResourceReservationsTask.perform();
  }

  // this gets called in the action menu when a row is selected
  @action
  selectReservation(reservation: ReservationLogRecord): void {
    if (this.selectedReservationId === reservation.id) {
      this.selectedReservationId = null;
    } else {
      this.selectedReservationId = reservation.id;
    }
  }

  @action
  clearSelectedReservation(): void {
    this.selectedReservationId = null;
  }

  @action
  setActivePage(page: 'desks' | 'parking'): void {
    this.activePage = page;
  }
}
